import type {
  CanvasRetrieve,
  CanvasCreateRequest,
  PatchedCanvasUpdateRequest,
  SharedLink,
  Instance,
  CanvasRecommendedFiles,
} from '@openapi'
import type { GetCanvasesParams, CanvasIndicator } from '@lib/types/canvases'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { CanvasThemeRetrieve } from '@lib/types/canvasTheme'
/**
 * Fetches a list of canvases to use in your app.
 *
 * @example
 * // The `filters` object is a reserved payload key to transfer the metadata dict over the wire.
 * api.getCanvases({
 *   search: 'my search query',
 *   ordering: '-created_at',
 *   filters: {
 *     metadata__mydaterangefiltername__range: ['2023-12-10', '2023-12-22'],
 *     metadata__mymultiselectfiltername: ['optionAValue', 'optionCValue'],
 *   },
 *  fields: 'id,name,metadata',
 * })
 */
export function getCanvases(
  payload: GetCanvasesParams & { filters?: Record<string, any>; fields?: string },
): Promise<PaginatedData<CanvasRetrieve>> {
  return this.API.request('get_canvases', payload)
}

export function createCanvas(payload: Omit<CanvasCreateRequest, 'instance_id'>): Promise<CanvasRetrieve> {
  return this.API.request('create_canvas', payload)
}

export function deleteCanvas(payload: { id: CanvasRetrieve['id'] }): Promise<CanvasRetrieve> {
  return this.API.request('delete_canvas', payload)
}

/**
 * Updates a canvas by ID
 *
 * @example
 * // The fields param is appended to the URL as a query param.
 * onMounted(() => {
 *   PitcherAPI.updateCanvas({
 *     id: '01HH4RCBH631K4JDHWAQB0RPR6',
 *     fields: 'id,name',
 *     name: 'To 3!',
 *   }).then((res) => {
 *     console.log(res)  // logs: { id: '01HH4RCBH631K4JDHWAQB0RPR6', name: 'To 3!' }
 *   })
 * })
 */
export function updateCanvas(
  payload: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; instance_id?: Instance['id']; fields?: string },
): Promise<CanvasRetrieve> {
  let shouldDebug = false
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    shouldDebug = urlParams.get('debug') === 'true' || !!(window as any).debug
  }
  shouldDebug && console.info('JS API UPDATE CANVAS', payload)

  return this.API.request('update_canvas', payload).then((res: any) => {
    shouldDebug && console.info('JS API UPDATE CANVAS RESPONSE', res)
    return res
  })
}

/**
 * Updates canvas indicators by canvas ID. It merges the passed object into existing canvas indicators adding new keys if they were empty and overriding pre-existing keys.
 *
 * Indicators can also be updated using updateCanvas API but in this case passed object fully replaces existing indicators.
 *
 * @example
 * // The fields param is appended to the URL as a query param.
 * onMounted(() => {
 *   PitcherAPI.updateCanvasIndicators({
 *     id: '01J9XT0WVXRTETF4CQZP42CPZP',
 *     indicators: {
 *       existing: { type: 'info', label: 'new label' }, // will be overriden
 *       new: { type: 'info', label: 'INFO' }, // will be added
 *       removeExisting: null // will be set to null and ignored, it is the same as removal
 *    }
 *    }).then((res) => {
 *     console.log(res)  // entire canvas object, including indicators field
 *   })
 * })
 */
export function updateCanvasIndicators(payload: {
  indicators: Record<string, CanvasIndicator>
  id: CanvasRetrieve['id']
  instance_id?: Instance['id']
}): Promise<CanvasRetrieve> {
  return this.API.request('update_canvas_indicators', payload)
}

export function getCanvas(payload: { id: CanvasRetrieve['id']; fields?: string }): Promise<CanvasRetrieve> {
  return this.API.request('get_canvas', payload)
}

export function shareCanvas(payload: { id: CanvasRetrieve['id'] }): Promise<SharedLink> {
  return this.API.request('share_canvas', payload)
}

export function getCanvasRecommendedFiles(payload: {
  canvas_id: CanvasRetrieve['id']
  match?: ('tags' | 'metadata')[]
}): Promise<CanvasRecommendedFiles> {
  return this.API.request('get_canvas_recommended_files', payload)
}

export function getCanvasTheme(payload: { canvas_id: CanvasRetrieve['id'] }): Promise<CanvasThemeRetrieve> {
  return this.API.request('get_canvas_theme', payload)
}

export function unassignCanvasTheme(payload: { canvas_id: CanvasRetrieve['id'] }): Promise<void> {
  return this.API.request('unassign_canvas_theme', payload)
}
